import React, { useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { RiMenu4Line, RiDashboardLine } from "react-icons/ri";
import { IoClose, IoGridOutline, IoPerson } from "react-icons/io5";
import { AiFillFund } from "react-icons/ai";
import { FaFileAlt, FaBloggerB } from "react-icons/fa";
import { FaQuora } from "react-icons/fa6";
import { TbBrandGoogleBigQuery } from "react-icons/tb";
import { BsBookmarkStarFill } from "react-icons/bs";
import { Edit } from "lucide-react";
import { IoPersonAdd } from "react-icons/io5";
import { FilePlusIcon } from 'lucide-react';

const menuItems = [
  { label: "Dashboard",        icon: <RiDashboardLine size={18} />,       path: "/dashboard",   permission: "dashboard"   },
  {label: "Add Role",             icon: <IoPersonAdd  size={18}/>  ,      path: "/staff",       permission:"staff"        },
  { label: "Add User",         icon: <IoPerson  size={18}/>,              path: "/user",        permission: "staff"    },
  { label: "Category",         icon: <IoGridOutline size={18} />,         path: "/category",    permission: "category"    },
  { label: "Products",         icon: <IoGridOutline size={18} />,         path: "/product",     permission: "product"     },
  { label: "Notice",          icon: <AiFillFund size={18} />,            path: "/carrier",     permission: "carrier"     },
  // { label: "Job Applications", icon: <FaFileAlt size={18} />,             path: "/job",         permission: "application" },
  { label: "Testimonial",      icon: <BsBookmarkStarFill size={18} />,    path: "/testimonial", permission: "admin"       },
  { label: "FAQ",              icon: <FaQuora size={18} />,               path: "/faq",         permission: "admin"       },
  { label: "Inquiries",        icon: <TbBrandGoogleBigQuery size={18} />, path: "/inquiry",     permission: "admin"       },
  { label: "Lead",             icon: <FilePlusIcon  size={18} />,       path: "/lead",        permission: "admin"       },
  // { label: "Banner",           icon: <Edit size={18} />,                  path: "/hero",        permission: "dashboard"   },
  { label: "Blog",             icon: <FaBloggerB size={18} />,            path: "/blog",        permission: "admin"       },
];

const SidebarContent = ({ navigate, pathname }) => {
  const permissions =
  JSON.parse(localStorage.getItem("permissions"))?.length
    ? JSON.parse(localStorage.getItem("permissions"))
    : ["*"];

  const filteredMenu = menuItems.filter(item =>
    permissions.includes("*") || permissions.includes(item.permission)
  );

  return (
    <div className="flex flex-col h-screen w-[220px] bg-white shadow-md px-3 pt-6 pb-4 overflow-y-auto">
      <ul className="space-y-1 tracking-wide flex-1">
        {filteredMenu.map((item) => {
          const isActive = pathname === item.path;
          return (
            <li key={item.path}>
              <div
                onClick={() => navigate(item.path)}
                className={`flex items-center text-base gap-3 cursor-pointer px-3 py-2 rounded-lg transition-colors duration-150 ${
                  isActive
                    ? "bg-green-50 text-green-700 font-semibold"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span className={isActive ? "text-green-600" : "text-gray-400"}>
                  {item.icon}
                </span>
                <span>{item.label}</span>
                {isActive && (
                  <span className="ml-auto w-1.5 h-1.5 rounded-full bg-green-500" />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
   const navigate = useNavigate();
  const { pathname } = useLocation();
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setIsSidebarOpen(false);
      }
    };
    if (isSidebarOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen, setIsSidebarOpen]);

  return (
    <React.Fragment>
      {/* Desktop sidebar */}
      <div className="hidden lg:block flex-shrink-0">
        <SidebarContent navigate={navigate} pathname={pathname} />
      </div>

      {/* Mobile */}
      <div className="block lg:hidden">
        <button
          onClick={() => setIsSidebarOpen((o) => !o)}
          className="absolute ml-6 mt-[-50px] text-white z-50"
          aria-label="Toggle sidebar"
        >
          {isSidebarOpen ? <IoClose size={30} /> : <RiMenu4Line size={30} />}
        </button>

        <AnimatePresence>
          {isSidebarOpen && (
            <>
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.3 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black z-40"
                onClick={() => setIsSidebarOpen(false)}
              />
              <motion.div
                key="drawer"
                ref={sidebarRef}
                className="fixed top-0 left-0 z-50 h-full"
                initial={{ opacity: 0, x: "-100%" }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: "-100%" }}
                transition={{ duration: 0.25 }}
              >
                <SidebarContent
                  navigate={(p) => { navigate(p); setIsSidebarOpen(false); }}
                  pathname={pathname}
                />
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </React.Fragment>
  );
};

export default Sidebar;